import React, {useState} from 'react'
import DragIcon from '../../../../../common/icons/DragIcon'
import LockIcon from '../../../../../common/icons/LockIcon'
import LockOpenIcon from '../../../../../common/icons/LockOpenIcon'
import TrashIcon from '../../../../../common/icons/TrashIcon'
import ImageThumbIcon from '../../../../../common/icons/ImageThumbIcon'
import Icon from '../../../../../common/Icon'
import {
    NODE_LAYER_ACTION_ICON_SIZE,
    NODE_LAYER_DEFAULT_SUBTITLE,
    NODE_LAYER_DEFAULT_TITLE,
    NODE_LAYER_IMAGE_PREVIEW_STYLE,
    NODE_LAYER_PRESENTATION,
    NODE_LAYER_SELECT_KEYS,
    NODE_LAYER_THUMBNAIL_ICON_SIZE,
    NODE_TYPES,
} from '../../../../constants'

function getNodePresentation(node, {subtitleVariant = 'list'} = {}) {
    const config = NODE_LAYER_PRESENTATION[node.type] || {}
    const isText = node.type === NODE_TYPES.TEXT
    const isImage = node.type === NODE_TYPES.IMAGE
    const imageSrc = node.content?.src ?? node.style?.src
    const title = isText ? node.content?.text || node.style?.text || config.defaultTitle : config.defaultTitle
    const subtitle = subtitleVariant === 'toolbox' ? config.toolboxSubtitle : config.listSubtitle

    return {
        title: title || NODE_LAYER_DEFAULT_TITLE,
        subtitle: subtitle || NODE_LAYER_DEFAULT_SUBTITLE,
        imageSrc,
        isImage,
        isText,
        locked: !!node.state?.locked,
        thumbnailIcon: config.thumbnailIcon || ImageThumbIcon,
    }
}

function DefaultDragAction() {
    return (
        <span className="SidebarPanel__LayerAction SidebarPanel__LayerAction--disabled" aria-label="Reorder">
            <Icon icon={DragIcon} width={NODE_LAYER_ACTION_ICON_SIZE} height={NODE_LAYER_ACTION_ICON_SIZE} />
        </span>
    )
}

function NodeLayerHeader({
    node,
    className,
    isSelected = false,
    subtitleVariant = 'list',
    dragControl = <DefaultDragAction />,
    onSelect,
    onRemove,
    onLockToggle,
}) {
    const [imageError, setImageError] = useState(false)
    const {title, subtitle, imageSrc, isImage, isText, locked, thumbnailIcon} = getNodePresentation(node, {
        subtitleVariant,
    })
    const showImagePreview = isImage && imageSrc && !imageError

    const handleSelect = () => {
        onSelect?.(node.id)
    }

    const handleKeyDown = (e) => {
        if (!NODE_LAYER_SELECT_KEYS.includes(e.key)) return
        e.preventDefault()
        handleSelect()
    }

    const handleRemove = (e) => {
        e.stopPropagation()
        onRemove?.(node.id)
    }

    const handleLockToggle = (e) => {
        e.stopPropagation()
        onLockToggle?.(node.id, !locked)
    }

    return (
        <div
            role={onSelect ? 'button' : undefined}
            tabIndex={onSelect ? 0 : undefined}
            className={`${className} ${isSelected ? 'is-selected' : ''} ${!isText ? 'SidebarPanel__LayerItem--image' : ''}`}
            onClick={onSelect ? handleSelect : undefined}
            onKeyDown={onSelect ? handleKeyDown : undefined}
        >
            <div className="SidebarPanel__LayerThumbnail">
                {showImagePreview ? (
                    <img
                        src={imageSrc}
                        alt=""
                        onError={() => setImageError(true)}
                        style={NODE_LAYER_IMAGE_PREVIEW_STYLE}
                    />
                ) : (
                    <Icon
                        icon={thumbnailIcon}
                        width={NODE_LAYER_THUMBNAIL_ICON_SIZE}
                        height={NODE_LAYER_THUMBNAIL_ICON_SIZE}
                    />
                )}
            </div>
            <div className="SidebarPanel__LayerMeta">
                <div className="SidebarPanel__LayerName">{title}</div>
                <div className={`SidebarPanel__LayerSubtitle ${!isText ? 'SidebarPanel__LayerSubtitle--image' : ''}`}>
                    {subtitle}
                </div>
            </div>
            <div className="SidebarPanel__LayerActions">
                {dragControl}
                <button
                    type="button"
                    className="SidebarPanel__LayerAction"
                    onClick={handleLockToggle}
                    aria-label={locked ? 'Unlock' : 'Lock'}
                >
                    <Icon
                        icon={locked ? LockIcon : LockOpenIcon}
                        width={NODE_LAYER_ACTION_ICON_SIZE}
                        height={NODE_LAYER_ACTION_ICON_SIZE}
                    />
                </button>
                <button type="button" className="SidebarPanel__LayerAction" onClick={handleRemove} aria-label="Delete">
                    <Icon icon={TrashIcon} width={NODE_LAYER_ACTION_ICON_SIZE} height={NODE_LAYER_ACTION_ICON_SIZE} />
                </button>
            </div>
        </div>
    )
}

export {DefaultDragAction, getNodePresentation}

export default NodeLayerHeader
